"use client";

import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useOnboarding } from "@/onboarding/OnboardingContext";
import { toast } from "sonner";
import { Check, Plus, Upload, FileText } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { DuckLoadingScreen } from "@/components/DuckLoadingScreen";
import { createProcess, CreateProcessInput } from "@/services/processService";

const SUGGESTED_PROCESSES = [
  { id: "hiring", name: "Hiring / Recruitment", reason: "Based on HR Data Labels and HR suppliers" },
  { id: "offboarding", name: "Employee Offboarding", reason: "Based on HR + IT suppliers and access controls" },
  { id: "supplier-payment", name: "Supplier Payment", reason: "Based on accounting and banking integrations" },
  { id: "expense-reimbursement", name: "Expense Reimbursement", reason: "Based on finance tools and staff size" },
  { id: "onboarding", name: "Employee Onboarding", reason: "Based on HR and identity providers" },
];

export default function ModalProcessesSuggestExample() {
  const [open, setOpen] = React.useState(true);
  const [query, setQuery] = React.useState("");
  const [selected, setSelected] = React.useState<string[]>([]);
  const [processes, setProcesses] = React.useState(SUGGESTED_PROCESSES);
  const [procMeta, setProcMeta] = React.useState<Record<string, { 
    pools: string[]; 
    suppliers: string[]; 
    systems: string[];
    processDocument?: string;  // Markdown content for the process document
    processType?: string;
    legalBasis?: string;
    frequency?: string;
  }>>({});
  const [saving, setSaving] = React.useState(false);
  const [showAddProcess, setShowAddProcess] = React.useState(false);
  const [newProcessName, setNewProcessName] = React.useState("");
  const [newProcessDescription, setNewProcessDescription] = React.useState("");
  const [newProcessFile, setNewProcessFile] = React.useState<File | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { setStatus, openStep, closeActive, goBack } = useOnboarding();

  const filtered = React.useMemo(() => processes.filter(p => p.name.toLowerCase().includes(query.toLowerCase())), [query, processes]);
  const toggle = (id: string) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const canContinue = selected.length > 0;
  const canAddProcess = newProcessName.trim().length > 2;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Accept .docx, .doc, .pdf, .txt, .md files
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'application/pdf',
        'text/plain',
        'text/markdown'
      ];
      const validExtensions = ['.docx', '.doc', '.pdf', '.txt', '.md'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      
      if (validTypes.includes(file.type) || hasValidExtension) {
        setNewProcessFile(file);
      } else {
        toast.error('Please upload a .docx, .doc, .pdf, .txt, or .md file');
      }
    }
  };

  const addCustomProcess = () => {
    if (!canAddProcess) return;
    
    const newId = `custom-${Date.now()}`;
    const newProcess = {
      id: newId,
      name: newProcessName.trim(),
      reason: newProcessDescription.trim() || 'Custom process added by user'
    };
    
    // Add to processes list
    setProcesses(prev => [newProcess, ...prev]);
    
    // Add metadata including the file if uploaded
    setProcMeta(prev => ({
      ...prev,
      [newId]: {
        pools: [],
        suppliers: [],
        systems: [],
        processDocument: newProcessDescription.trim() || `### ${newProcessName.trim()}\n\nCustom process document.`,
        processType: 'Custom',
        customFile: newProcessFile // Store the file reference
      } as any
    }));
    
    // Auto-select the new process
    setSelected(prev => [...prev, newId]);
    
    // Reset form
    setNewProcessName("");
    setNewProcessDescription("");
    setNewProcessFile(null);
    setShowAddProcess(false);
    
    toast.success(`Added "${newProcess.name}" to processes`);
  };

  React.useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('onboardingAI.lastResponse') : null;
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const payload = parsed?.data?.payload;
      const items = payload?.processes;
      if (Array.isArray(items) && items.length > 0) {
        const mapped = items.map((d: any) => ({
          id: d.id || d.name,
          name: d.name,
          reason: d.description || (d.process_type ? `Type: ${d.process_type}` : (d.trigger ? `Trigger: ${d.trigger}` : 'Suggested')),
        }));
        setProcesses(mapped);
        setSelected(mapped.map((m: any) => m.id));

        const poolsArr: any[] = Array.isArray(payload?.data_pools) ? payload.data_pools : [];
        const suppliersList: any[] = (payload?.query?.suppliers && Array.isArray(payload.query.suppliers)) ? payload.query.suppliers : (Array.isArray(parsed?.data?.query?.suppliers) ? parsed.data.query.suppliers : []);
        const poolById = new Map<string, any>(poolsArr.map((p: any) => [p.id, p]));
        const supplierNameById = new Map<string, string>(suppliersList.map((s: any) => [s.id, s.name]));
        const meta: Record<string, { 
          pools: string[]; 
          suppliers: string[]; 
          systems: string[];
          processDocument?: string;
          processType?: string;
          legalBasis?: string;
          frequency?: string;
        }> = {};
        items.forEach((d: any) => {
          const poolNames = (Array.isArray(d?.data_pool_ids) ? d.data_pool_ids.map((id: string) => poolById.get(id)?.name).filter(Boolean) : []) as string[];
          const supplierNames = (Array.isArray(d?.supplier_ids) ? d.supplier_ids.map((sid: string) => supplierNameById.get(sid)).filter(Boolean) : []) as string[];
          const systems = Array.from(new Set(
            (Array.isArray(d?.data_pool_ids) ? d.data_pool_ids : [])
              .flatMap((id: string) => {
                const p = poolById.get(id);
                return Array.isArray(p?.primary_systems) ? p.primary_systems : [];
              })
          )) as string[];
          meta[d.id || d.name] = {
            pools: poolNames,
            suppliers: supplierNames,
            systems,
            processDocument: d.process_document || undefined,
            processType: d.process_type || undefined,
            legalBasis: d.legal_basis || undefined,
            frequency: d.frequency || undefined,
          };
        });
        setProcMeta(meta);
      }
    } catch {}
  }, []);

  return (
    <div className="space-y-4">
      <Button onClick={() => setOpen(true)}>Open Processes Suggest</Button>
      <Dialog open={open} onOpenChange={(v) => { if (!saving) { setOpen(v); if (!v) closeActive(); } }}>
        <DialogContent className="max-w-7xl! w-7xl! h-screen bg-transparent shadow-none border-0 p-0 flex items-center justify-center">
          <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
            {/* Close button */}
            <button
              onClick={() => { if (!saving) { setOpen(false); closeActive(); } }}
              className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Close"
              disabled={saving}
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {/* Duck Loading Screen Overlay */}
            {saving && (
              <div className="absolute inset-0 z-50 rounded-xl overflow-hidden">
                <DuckLoadingScreen 
                  variant="overlay"
                  title="Saving Processes..." 
                  text="Adding your selected processes to your process register"
                  duckFacts={[
                    "Quack! Saving your processes...",
                    "Adding processes to your register...",
                    "Setting up process tracking...",
                    "Almost there! Finalising your processes...",
                    "Duck is organising... almost done!"
                  ]}
                />
              </div>
            )}
            
            <div className="p-6 pb-4 border-b border-white/10 bg-linear-to-b from-primary/5 to-transparent">
              <h2 className="text-3xl font-bold text-foreground">Suggested Processes</h2>
              <p className="mt-2 text-muted-foreground">Select the processes in your business that involve collecting, using, storing or sharing information even if they aren't formally documented. If none of the suggestions fit, add a custom process.</p>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                  <Input placeholder="Search processes..." value={query} onChange={(e) => setQuery(e.target.value)} className="bg-white/10 border-white/20 sm:max-w-sm" />
                  <div className="text-xs text-muted-foreground">{selected.length} selected</div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowAddProcess(!showAddProcess)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Custom Process
                </Button>
              </div>

              {/* Add Custom Process Form */}
              {showAddProcess && (
                <div className="rounded-xl border-2 border-dashed border-primary/50 bg-primary/5 p-4 space-y-4">
                  <div className="flex items-center gap-2 text-primary font-medium">
                    <Plus className="h-5 w-5" />
                    Add Your Own Process
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="processName">Process Name *</Label>
                      <Input 
                        id="processName"
                        placeholder="e.g., Customer Data Request Handling" 
                        value={newProcessName} 
                        onChange={(e) => setNewProcessName(e.target.value)} 
                        className="bg-white/10 border-white/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="processFile">Upload Document (optional)</Label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".docx,.doc,.pdf,.txt,.md"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <Button 
                        variant="outline" 
                        className="w-full justify-start gap-2"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {newProcessFile ? (
                          <>
                            <FileText className="h-4 w-4" />
                            {newProcessFile.name}
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Choose file...
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="processDesc">Description / Process Steps</Label>
                    <Textarea 
                      id="processDesc"
                      placeholder="Describe the process steps or paste your process documentation here..."
                      value={newProcessDescription}
                      onChange={(e) => setNewProcessDescription(e.target.value)}
                      className="bg-white/10 border-white/20 min-h-[100px]"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => {
                      setShowAddProcess(false);
                      setNewProcessName("");
                      setNewProcessDescription("");
                      setNewProcessFile(null);
                    }}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={addCustomProcess} disabled={!canAddProcess}>
                      Add Process
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filtered.map(p => {
                  const isSelected = selected.includes(p.id);
                  const isCustom = p.id.startsWith('custom-');
                  return (
                    <div
                      key={p.id}
                      onClick={() => toggle(p.id)}
                      style={{
                        background: isSelected ? 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.15), rgba(var(--primary-rgb), 0.05))' : '',
                        backdropFilter: 'blur(8px)'
                      }}
                      className={`relative overflow-hidden rounded-xl p-4 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                        isSelected
                          ? 'border-2 border-primary shadow-lg'
                          : 'bg-white/5 hover:bg-white/10 dark:bg-gray-800/30 dark:hover:bg-gray-800/50 border border-white/10 shadow-sm hover:shadow-md'
                      }`}
                    >
                      {/* Light accent when selected */}
                      {isSelected && (
                        <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 via-transparent to-primary/10 pointer-events-none" />
                      )}
                      
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute top-3 right-3 h-6 w-6 rounded-full bg-primary flex items-center justify-center shadow-sm z-20">
                          <Check className="h-3 w-3 text-primary-foreground" />
                        </div>
                      )}
                      
                      {/* Custom badge */}
                      {isCustom && (
                        <Badge variant="secondary" className="absolute top-3 left-3 text-[10px]">Custom</Badge>
                      )}
                      
                      <div className={`relative z-10 ${isCustom ? 'mt-6' : ''}`}>
                        <div className="text-foreground font-medium">{p.name}</div>
                        <div className="text-xs text-muted-foreground mt-1">{p.reason}</div>
                        {/* {procMeta[p.id]?.pools && procMeta[p.id].pools.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1 items-center">
                            <span className="text-[10px] text-muted-foreground mr-1">Data pools</span>
                            {procMeta[p.id].pools.map((pool) => (
                              <Badge key={pool} variant="policy">{pool}</Badge>
                            ))}
                          </div>
                        )}
                        {procMeta[p.id]?.suppliers && procMeta[p.id].suppliers.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1 items-center">
                            <span className="text-[10px] text-muted-foreground mr-1">Suppliers</span>
                            {procMeta[p.id].suppliers.map((s) => (
                              <Badge key={s} variant="service">{s}</Badge>
                            ))}
                          </div>
                        )}
                        {procMeta[p.id]?.systems && procMeta[p.id].systems.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1 items-center">
                            <span className="text-[10px] text-muted-foreground mr-1">Primary systems</span>
                            {procMeta[p.id].systems.map((sys) => (
                              <Badge key={sys} variant="software">{sys}</Badge>
                            ))}
                          </div>
                        )} */}
                        {/* Show file indicator for custom processes with uploaded files */}
                        {(procMeta[p.id] as any)?.customFile && (
                          <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                            <FileText className="h-3 w-3" />
                            {(procMeta[p.id] as any).customFile.name}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* {selected.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selected.map(id => {
                    const p = processes.find(x => x.id === id);
                    if (!p) return null;
                    return <Badge key={id} variant="outline" className="text-xs">{p.name}</Badge>
                  })}
                </div>
              )} */}
            </div>

            <div className="border-t border-white/10 px-6 py-4 bg-linear-to-t from-primary/5 to-transparent flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setOpen(false); goBack(); }} disabled={saving}>Back</Button>
              <Button disabled={!canContinue || saving} onClick={async () => {
                setSaving(true);
                try {
                  const selectedProcesses = processes.filter(p => selected.includes(p.id));
                  const companyID = (window as any).companyID;
                  
                  if (!companyID) {
                    throw new Error('Company ID not found');
                  }
                  
                  // Create processes for each selected process (documented if file attached, verbal otherwise)
                  const processesCreated: string[] = [];
                  for (const proc of selectedProcesses) {
                    const meta = procMeta[proc.id] as any;
                    
                    try {
                      // Build description from metadata
                      // NOTE: We intentionally do NOT use meta.processDocument (AI-suggested process steps)
                      // All processes should be added as verbal by default - customers can upload their own
                      // documentation after onboarding if the process is documented.
                      // This avoids liability issues with AI-generated process advice.
                      let description = proc.reason || '';
                      const isCustomProcess = proc.id.startsWith('custom-');
                      
                      // For custom processes, use the user-entered description
                      // For AI-suggested processes, just use the short reason (e.g., "Based on HR Data Labels")
                      if (isCustomProcess && meta?.processDocument) {
                        // Only use processDocument for custom processes where user entered it
                        description = meta.processDocument;
                      }
                      
                      // Determine process type:
                      // - If user attached a document (customFile) -> 'documented'
                      // - Otherwise -> 'verbal'
                      const hasAttachedDocument = !!(meta?.customFile);
                      const processType = hasAttachedDocument ? 'documented' : 'verbal';
                      
                      const processInput: CreateProcessInput = {
                        name: proc.name,
                        description: description.trim(),
                        type: processType,
                        status: 'Draft',
                        category: meta?.processType || 'General',
                        // If there's an attached file, include it for documented processes
                        file: hasAttachedDocument ? meta.customFile : undefined,
                      };
                      
                      await createProcess(companyID, processInput);
                      processesCreated.push(proc.name);
                      console.log(`[ProcessesSuggest] Created ${processType} process: ${proc.name}`);
                    } catch (procError) {
                      console.error(`[ProcessesSuggest] Failed to create process ${proc.name}:`, procError);
                    }
                  }
                  
                  // Save to localStorage for legacy support
                  try { 
                    localStorage.setItem("onboardingAI.accepted.processes", JSON.stringify(selectedProcesses)); 
                  } catch {}
                  
                  if (processesCreated.length > 0) {
                    toast.success(`Created ${processesCreated.length} process(es)`);
                  }
                  
                  setStatus("add_processes", "done");
                  setOpen(false);
                  openStep("data_labels");
                } catch (error) {
                  console.error('[ProcessesSuggest] Error saving processes:', error);
                  toast.error('Failed to save processes');
                } finally {
                  setSaving(false);
                }
              }}>
                Accept & Next
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
